import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Skeleton } from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { createGlobalStyle, styled } from 'styled-components'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import { Freshness, Message, Panel, PanelBody, PanelHeader, RefreshButton } from './styles'

interface ReadinessItem {
  name: string
  workingDays: string
  baseDate: string | null
  deliveryDate: string | null
}

interface ReadinessReference {
  items: ReadinessItem[]
  fetchedAt: string
  stale: boolean
}

interface ReadinessReferencePanelProps {
  orderId: string | number
  orderNotFound?: boolean
  onCheckOrder?: () => void
  checkingOrder?: boolean
  orderLookupError?: string
}

const ReferenceGridLayout = createGlobalStyle`
  .ordersForm .ordersForm__field-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(440px, 480px);
    gap: 26px;
  }

  @media (max-width: 1799px) {
    .ordersForm .ordersForm__field-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(420px, 450px);
    }
  }

  @media (max-width: 1399px) {
    .ordersForm .ordersForm__field-grid {
      grid-template-columns: minmax(0, 1fr) minmax(420px, 430px);
    }
  }

  @media (max-width: 1099px) {
    .ordersForm .ordersForm__field-grid {
      display: block;
    }
  }
`

const Introduction = styled.p`
  margin: 13px 0 10px;
  color: #596777;
  font-size: 12px;
  line-height: 1.5;
`

const DetailsTrigger = styled(Button)`
  && {
    height: 32px;
    margin: 0 0 12px;
    padding: 0 14px;
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    font-size: 12px;
    font-weight: 600;
  }

  &&:hover,
  &&:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    opacity: 0.9;
  }
`

const Search = styled(Input)`
  margin-bottom: 10px;
`

const TableScroller = styled.div`
  max-height: 570px;
  overflow: auto;
  scrollbar-width: thin;
`

const ReferenceTable = styled.table`
  width: 100%;
  min-width: 390px;
  border-collapse: collapse;
  table-layout: fixed;
  color: #343b45;
  font-size: 12px;
  line-height: 1.4;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 8px 8px 9px 0;
    border-bottom: 1px solid #dfe5ed;
    background: ${({ theme }) => theme.colors.white};
    color: #697484;
    font-size: 11px;
    font-weight: 600;
    text-align: left;
  }

  td {
    padding: 11px 8px 11px 0;
    border-bottom: 1px solid #e9edf2;
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  th:nth-child(1) {
    width: 51%;
  }
  th:nth-child(2) {
    width: 22%;
  }
  th:nth-child(3) {
    width: 27%;
  }

  td:nth-child(2) {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    white-space: nowrap;
  }

  td:nth-child(3) {
    white-space: nowrap;
  }
`

const Caption = styled.p`
  margin: 13px 0 0;
  color: #697484;
  font-size: 11px;
  line-height: 1.5;
`

const InfoModal = styled(Modal)`
  .ant-modal-body {
    max-height: min(68vh, 620px);
    overflow-y: auto;
  }
`

const InfoSection = styled.section`
  padding: 14px 0;
  border-top: 1px solid #e9edf2;

  &:first-child {
    padding-top: 0;
    border-top: 0;
  }

  h3 {
    margin: 0 0 7px;
    color: #303a47;
    font-size: 14px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #536174;
    line-height: 1.55;
  }

  ul {
    margin: 7px 0 0;
    padding-left: 19px;
    color: #536174;
    line-height: 1.65;
  }
`

const formatDate = (value: string | null): string => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '—'
}

const formatDateTime = (value: string): string => {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
}

export const ReadinessReferencePanel: React.FC<ReadinessReferencePanelProps> = memo(
  ({ orderId, orderNotFound = false, onCheckOrder, checkingOrder = false, orderLookupError }) => {
    const [data, setData] = useState<ReadinessReference | null>(null)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState('')
    const [query, setQuery] = useState('')
    const [detailsOpen, setDetailsOpen] = useState(false)

    const load = useCallback(
      async (refresh: boolean, signal?: AbortSignal) => {
        if (refresh) setRefreshing(true)
        else setLoading(true)
        setError('')
        try {
          const suffix = refresh ? '?refresh=1' : ''
          const response = await fetchAPI(
            `/api/order/${orderId}/windraw/readiness-reference${suffix}`,
            { signal, timeoutMs: 45_000 },
            true,
          )
          if (signal?.aborted) return
          if (response.error) {
            setError(response.error.message || 'Не удалось получить сроки из WinDraw')
          } else {
            setData(response as unknown as ReadinessReference)
          }
        } catch {
          if (!signal?.aborted) setError('Не удалось получить сроки из WinDraw')
        } finally {
          if (!signal?.aborted) {
            setLoading(false)
            setRefreshing(false)
          }
        }
      },
      [orderId],
    )

    useEffect(() => {
      const controller = new AbortController()
      setData(null)
      load(false, controller.signal)
      return () => controller.abort()
    }, [load])

    const filteredItems = useMemo(() => {
      const normalized = query.trim().toLocaleLowerCase('ru-RU')
      return (
        data?.items.filter(item => item.name.toLocaleLowerCase('ru-RU').includes(normalized)) || []
      )
    }, [data, query])

    return (
      <>
        <ReferenceGridLayout />
        <Panel aria-label="Ориентировочные сроки по типам изделий">
          <PanelHeader>
            <h3>Сроки по типам изделий</h3>
            <RefreshButton
              type="text"
              size="small"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={() => load(true)}
              disabled={loading || refreshing}
              aria-label="Пересчитать сроки в WinDraw"
              title="Пересчитать сроки в WinDraw"
            />
          </PanelHeader>
          <PanelBody>
            <Introduction>
              {orderNotFound
                ? 'Заказ пока не найден в производственном WinDraw, ниже показаны ориентиры по типам изделий'
                : 'Производственный заказ не указан: выберите тип изделия, чтобы назвать клиенту ориентир'}
            </Introduction>
            <DetailsTrigger type="primary" onClick={() => setDetailsOpen(true)}>
              Подробнее о сроках
            </DetailsTrigger>
            {orderNotFound && onCheckOrder && (
              <Button type="link" size="small" loading={checkingOrder} onClick={onCheckOrder}>
                Проверить заказ в WinDraw
              </Button>
            )}
            {orderLookupError && (
              <Message $error role="alert">
                Не удалось повторно проверить производственный заказ в WinDraw
              </Message>
            )}
            {error && (
              <Message $error role="alert">
                {data ? 'Не удалось обновить сроки, показан предыдущий расчёт' : error}
              </Message>
            )}
            {data?.stale && (
              <Message role="status">Сервис расчёта недоступен, показаны сохранённые сроки</Message>
            )}
            {loading && !data ? (
              <Skeleton active title={false} paragraph={{ rows: 7 }} />
            ) : data ? (
              <>
                <Search
                  size="small"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Найти изделие"
                  prefix={<SearchOutlined />}
                  allowClear
                  aria-label="Найти тип изделия"
                />
                <TableScroller>
                  <ReferenceTable aria-label="Сроки по типам изделий">
                    <thead>
                      <tr>
                        <th scope="col">Изделие</th>
                        <th scope="col">Дата доставки</th>
                        <th scope="col">Рабочих дней</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map(item => (
                        <tr key={item.name}>
                          <td>{item.name}</td>
                          <td>{formatDate(item.deliveryDate)}</td>
                          <td>{item.workingDays || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ReferenceTable>
                </TableScroller>
                {filteredItems.length === 0 && <Message>Тип изделия не найден</Message>}
                <Caption>
                  Дата рассчитана WinDraw по контрольным заказам; диапазон рабочих дней носит
                  справочный характер; состав конкретного заказа не учитывается
                </Caption>
                <Freshness>
                  {data.stale ? 'Последний расчёт' : 'Рассчитано'} ·{' '}
                  {formatDateTime(data.fetchedAt)}
                </Freshness>
              </>
            ) : null}
          </PanelBody>
        </Panel>
        <InfoModal
          title="Подробнее о сроках"
          open={detailsOpen}
          onCancel={() => setDetailsOpen(false)}
          footer={null}
          width={560}
        >
          <InfoSection>
            <h3>Крупные и разноцветные заказы</h3>
            <p>
              Для заказов площадью более 30 м² и изделий в разных цветах срок изготовления может
              увеличиться
            </p>
          </InfoSection>
          <InfoSection>
            <h3>Ламинированные изделия в массе</h3>
            <p>Для стандартных цветов VEKA действует обычный срок по ламинации</p>
            <ul>
              <li>Основные цвета VEKA — дополнительно 10 рабочих дней</li>
              <li>Дополнительные цвета VEKA — дополнительно 30 рабочих дней</li>
            </ul>
          </InfoSection>
          <InfoSection>
            <h3>Закалённые стёкла</h3>
            <p>Срок изготовления изделий с закалёнными стёклами — от 18 рабочих дней</p>
          </InfoSection>
        </InfoModal>
      </>
    )
  },
)

ReadinessReferencePanel.displayName = 'ReadinessReferencePanel'
